import { sheetsUiOptionId } from '@sharedlib/common/sheets/ui-commands';

import { type DownloadAction } from './download-menu.component';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import { BORDER_STYLES, FONT_FAMILIES, PALETTE, TOOLBAR, type MenuEntry, type ToolbarControl } from '@sharedlib/sheets-editor/toolbar-spec';

/**
 * The editor's registered surfaces and controls, by the ids the MCP contract's UI tools use (6 Oct: "Use stable
 * registered surface/control IDs and typed values, not arbitrary DOM access"). Built from the same data the
 * toolbar is drawn from (toolbar-spec.ts) and the same menu the header shows, so an option exists here exactly
 * when a person can click it.
 *
 * Kept free of Angular and the engine, so it is testable alone and stays out of the engine's chunk.
 */

const S = SHEETS_STRINGS;

/** What choosing an option does: a menu entry runs, a swatch picks its colour, a line style is set for borders. */
export type UiOptionAction =
  | { readonly kind: 'entry'; readonly entry: Extract<MenuEntry, { label: string }> }
  | { readonly kind: 'colour'; readonly picker: 'text' | 'fill' | 'border'; readonly colour: string }
  | { readonly kind: 'line'; readonly style: string }
  | { readonly kind: 'font'; readonly family: string }
  | { readonly kind: 'download'; readonly action: DownloadAction };

export interface UiOption {
  readonly id: string;
  readonly label: string;
  readonly action: UiOptionAction;
}

/** A toolbar control by its id. */
export function toolbarControl(id: string): ToolbarControl | null {
  return (TOOLBAR.find((c) => !('separator' in c) && c.id === id) as ToolbarControl | undefined) ?? null;
}

/** A toolbar menu's options, in the menu's order: its entries, then any swatches and line styles. */
export function menuOptions(controlId: string): readonly UiOption[] {
  const control = toolbarControl(controlId);
  const options: UiOption[] = [];
  for (const entry of control?.menu ?? []) {
    if ('label' in entry) options.push({ id: sheetsUiOptionId(entry.label), label: entry.label, action: { kind: 'entry', entry } });
    else if ('picker' in entry) {
      for (const colour of PALETTE) options.push({ id: sheetsUiOptionId(colour), label: colour, action: { kind: 'colour', picker: entry.picker, colour } });
    } else if ('lineStyle' in entry) {
      for (const style of BORDER_STYLES) options.push({ id: `line-${sheetsUiOptionId(style.value)}`, label: style.label, action: { kind: 'line', style: style.value } });
    }
  }
  return options;
}

/** The font select's options. */
export function fontOptions(families: readonly string[] = FONT_FAMILIES): readonly UiOption[] {
  return families.map((family) => ({ id: sheetsUiOptionId(family), label: family, action: { kind: 'font', family } }));
}

/** "Download and share", as `download-menu.component.html` lists it; "Share via link" only for a workbook. */
export function downloadOptions(share: boolean): readonly UiOption[] {
  const all: [DownloadAction, string][] = [
    ['xlsx', S['Excel workbook (.xlsx)']],
    ['xls', S['Excel 97–2003 (.xls) · values only']],
    ['ods', S['OpenDocument (.ods)']],
    ['pdf', S['PDF document (.pdf)…']],
    ['csv', S['Current sheet (.csv)']],
    ['tsv', S['Current sheet (.tsv)']],
    ['import', S['Import workbook…']],
    ['import-csv', S['Import CSV as a new sheet…']],
    ['share', S['Share via link']],
    ['shortcuts', S['Keyboard shortcuts']],
  ];
  return all.filter(([action]) => share || action !== 'share').map(([action, label]) => ({ id: action, label, action: { kind: 'download', action } }));
}

/**
 * Options an agent can't take, though a person can, and why: each needs the person's own click, which the
 * browser asks for before it opens a file chooser or goes fullscreen. Refused by name rather than started and
 * left to fail where nobody sees it (the contract: "Do not silently pretend to click").
 */
export const NEEDS_THE_PERSON: Readonly<Record<string, string>> = {
  'menu.insert/floating-image': 'choosing an image needs the person’s own click (the browser’s file chooser)',
  'menu.insert/image-in-cell': 'choosing an image needs the person’s own click (the browser’s file chooser)',
  'menu.more/toggle-fullscreen': 'the browser allows fullscreen only from the person’s own click',
  'menu.download/import-csv': 'choosing a file needs the person’s own click (the browser’s file chooser)',
};
